import { useCallback, useEffect, useRef, useState } from "react";

import { useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { useRouter } from "@tanstack/react-router";

import { useAlertsExhaustedMessage } from "@/lib/hooks/use-alerts-exhausted";
import { showToast } from "@/lib/ui/toast";

/**
 * Facebook pending link — the portal half (2026-10).
 *
 * Facebook's email is no proof of the mailbox, so when a Facebook sign-in's
 * email matches an existing PontiMed account apps/api links nothing and signs
 * nobody in. It parks the link (15 min, single use, bound to this browser by
 * an HttpOnly cookie) and redirects to /auth/error with
 * `?error=oauth_link_requires_email_code&link=<token>`. The person then signs
 * in to that account the normal way — an email code — and the link is
 * completed under that session.
 *
 * Hook point: the error page remembers the token here; `AuthModalProvider`
 * (mounted for the whole app) renders `PendingOAuthLinkPrompt`, whose hook
 * (`usePendingOAuthLinkPrompt`) picks the link up the moment a session
 * exists. That covers both ways the email sign-in can finish: the code typed
 * in the modal, and the magic link opened from the mail client in a new tab —
 * hence `localStorage`, like the profile-completion resume marker.
 *
 * Nothing is connected without asking (2026-10): the signed-in person sees
 * "Connect the Facebook account {name} to your PontiMed account?" — the
 * Facebook account's own name (2026-10-09 review: the address that matched is
 * the person's OWN, so it told them nothing about whose Facebook account this
 * is) — and only "Connect" completes it; "Not now" (or closing the sheet) discards the link
 * on the server too. It used to complete silently on the first session — a
 * stray marker, or a link parked by someone else in this browser, became a
 * sign-in method of whoever signed in next.
 */

const PENDING_LINK_KEY = "pontimed_pending_oauth_link";
/** Same lifetime as the server-side record. */
const PENDING_LINK_TTL_MS = 15 * 60 * 1000;

interface StoredPendingLink {
  token: string;
  expiresAt: number;
}

/** Remember the parked link until a session can complete it. */
export function rememberPendingOAuthLink(token: string): void {
  if (typeof window === "undefined") return;
  try {
    const value: StoredPendingLink = { token, expiresAt: Date.now() + PENDING_LINK_TTL_MS };
    window.localStorage.setItem(PENDING_LINK_KEY, JSON.stringify(value));
  } catch {
    /* Safari private mode — the link just won't be completed */
  }
}

function readPendingOAuthLink(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PENDING_LINK_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredPendingLink>;
    if (typeof value.token !== "string" || typeof value.expiresAt !== "number") return null;
    if (value.expiresAt <= Date.now()) {
      window.localStorage.removeItem(PENDING_LINK_KEY);
      return null;
    }
    return value.token;
  } catch {
    return null;
  }
}

function forgetPendingOAuthLink(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PENDING_LINK_KEY);
  } catch {
    /* ignore */
  }
}

/** A parked link as /pending-link/peek describes it. */
export interface PendingOAuthLinkInfo {
  provider: string;
  /** The address that matched (the account's own) — the email-code prefill. */
  email: string;
  /** The Facebook account's name; null when it gave none (or an older API answered). */
  displayName?: string | null;
}

/** The parked link's provider, address and Facebook name. Null when gone. */
export async function peekPendingOAuthLink(token: string): Promise<PendingOAuthLinkInfo | null> {
  try {
    return await apiFetch.post<PendingOAuthLinkInfo>(
      "/api/auth/oauth/pending-link/peek",
      { token },
      { skipAuthRefresh: true },
    );
  } catch {
    return null;
  }
}

/** A parked link waiting for the signed-in person's answer. */
interface PendingPrompt {
  token: string;
  provider: string;
  email: string;
  displayName: string | null;
}

export interface PendingOAuthLinkPromptState {
  /** Whether the "Connect …?" sheet is up. */
  open: boolean;
  /** The address that matched — the account's own (kept after closing, for the exit animation). */
  email: string | null;
  /** The Facebook account's own name, when Facebook gave one — what tells whose account it is. */
  displayName: string | null;
  /** The completion request is in flight. */
  busy: boolean;
  /** "Connect": complete the link under the current session. */
  connect: () => void;
  /** "Not now" / closing the sheet: discard the link, here and on the server. */
  decline: () => void;
}

/**
 * Ask before completing a remembered pending link. Mounted once, through
 * `PendingOAuthLinkPrompt` in `AuthModalProvider`. Checks at most once per
 * signed-in account: a session of ANOTHER account is refused by the server
 * without spending the link, so the marker stays for the right account.
 */
export function usePendingOAuthLinkPrompt(): PendingOAuthLinkPromptState {
  const { t } = useLingui();
  const alertsExhaustedMessage = useAlertsExhaustedMessage();
  const { status, user } = useSession();
  const router = useRouter();
  const checkedFor = useRef<string | null>(null);
  const [prompt, setPrompt] = useState<PendingPrompt | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status !== "authenticated" || !user?.userId) return;
    if (checkedFor.current === user.userId) return;
    const token = readPendingOAuthLink();
    if (!token) return;
    checkedFor.current = user.userId;
    // No cancellation flag: the provider lives for the whole app session, and
    // a StrictMode double effect would drop the answer (the ref above stops
    // the second run).
    void peekPendingOAuthLink(token).then((link) => {
      if (!link) {
        forgetPendingOAuthLink();
        showToast(
          t`This request to connect Facebook has expired. Continue with Facebook again to retry.`,
          "error",
        );
        return;
      }
      setPrompt({
        token,
        provider: link.provider,
        email: link.email,
        displayName: link.displayName ?? null,
      });
      setOpen(true);
    });
  }, [status, user?.userId, t]);

  const connect = useCallback(() => {
    if (!prompt || busy) return;
    setBusy(true);
    const toast = showToast(t`Connecting Facebook…`, "loading");
    void (async () => {
      try {
        await apiFetch.post("/api/auth/oauth/pending-link/complete", { token: prompt.token });
        forgetPendingOAuthLink();
        setOpen(false);
        toast.update(t`Facebook is now connected to your account`, "success");
        // Landed on an /auth/* page (already signed in when the error page
        // loaded): take them where the new sign-in method is listed.
        if (window.location.pathname.startsWith("/auth/")) {
          void router.navigate({
            to: "/account/user/settings",
            search: { tab: "accounts" } as never,
            replace: true,
          });
        }
      } catch (err) {
        setOpen(false);
        const code =
          err instanceof ApiError
            ? (err.body as { error?: string } | null | undefined)?.error
            : undefined;
        if (code === "pending_link_wrong_account") {
          // Not spent server-side: keep it for the account it belongs to.
          toast.update(
            t`You're signed in to a different account, so Facebook wasn't connected. Sign in with the account that uses this email.`,
            "error",
          );
          return;
        }
        forgetPendingOAuthLink();
        const exhaustedMessage = alertsExhaustedMessage(err);
        if (exhaustedMessage) {
          toast.update(exhaustedMessage, "error");
        } else if (code === "provider_already_linked") {
          toast.update(t`A Facebook account is already connected to this account.`, "error");
        } else if (code === "identity_taken") {
          toast.update(
            t`This Facebook account is already connected to another PontiMed account.`,
            "error",
          );
        } else if (code === "oauth_link_requires_step_up") {
          toast.update(
            t`This account uses two-factor authentication. Connect Facebook from Settings → Accounts.`,
            "error",
          );
        } else if (code === "pending_link_not_found") {
          toast.update(
            t`This request to connect Facebook has expired. Continue with Facebook again to retry.`,
            "error",
          );
        } else {
          toast.update(t`Couldn't connect Facebook. Please try again.`, "error");
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [prompt, busy, t, router, alertsExhaustedMessage]);

  const decline = useCallback(() => {
    if (!prompt || busy) return;
    setOpen(false);
    forgetPendingOAuthLink();
    // Drops the parked link and its cookie. Best-effort: unanswered, the link
    // still expires on its own within 15 minutes.
    void apiFetch
      .post(
        "/api/auth/oauth/pending-link/discard",
        { token: prompt.token },
        { skipAuthRefresh: true },
      )
      .catch(() => {});
  }, [prompt, busy]);

  return {
    open,
    email: prompt?.email ?? null,
    displayName: prompt?.displayName ?? null,
    busy,
    connect,
    decline,
  };
}
